import { useEffect, useState } from 'react';
import { Modal } from './Modal';
import { Icon } from './Icon';
import type { IconName } from './Icon';

interface TutorialModalProps {
  open: boolean;
  onClose: () => void;
  replay?: boolean;
}

interface TutorialStep {
  icon: IconName;
  kicker: string;
  title: string;
  text: string;
  chips: string[];
}

const STEPS: TutorialStep[] = [
  {
    icon: 'flag',
    kicker: '01 · ATERRIZA',
    title: 'Cuatro unidades, un Núcleo',
    text: 'Tu escuadrón empieza en el muelle. Saca una unidad con un 5, recorre la isla y entra en el Núcleo con la tirada exacta. Gana quien sincroniza sus cuatro unidades primero.',
    chips: ['2–4 escuadrones', 'Salida con 5', 'Meta exacta'],
  },
  {
    icon: 'dice',
    kicker: '02 · TIRA',
    title: 'El dado decide. Tú eliges.',
    text: 'El servidor lanza el dado y valida cada movimiento. Después elige qué unidad mueve. Un 6 concede otra tirada; tres seises consecutivos castigan a tu unidad más avanzada.',
    chips: ['Dado autoritativo', '6 = extra', 'Sin trampas'],
  },
  {
    icon: 'portal',
    kicker: '03 · LEE LA ISLA',
    title: 'Portales, cofres y anomalías',
    text: 'Las celdas especiales tienen escena propia. El portal te impulsa 3 celdas, el cofre revela una anomalía y la fisura te devuelve 3. Las zonas seguras bloquean capturas.',
    chips: ['Portal', 'Cofre', 'Zona segura'],
  },
  {
    icon: 'shield',
    kicker: '04 · VUELVE',
    title: 'Tu sitio te espera',
    text: 'Si se cae la conexión, tu asiento se conserva y el estado se recupera. Ajusta música, efectos y volumen desde el engranaje. Puedes repetir este tutorial desde el menú.',
    chips: ['Reconexión', 'Audio', 'Accesibilidad'],
  },
];

export function TutorialModal({ open, onClose, replay = false }: TutorialModalProps) {
  const [step, setStep] = useState(0);
  const current = STEPS[step] ?? STEPS[0]!;
  const last = step === STEPS.length - 1;

  useEffect(() => {
    if (open) setStep(0);
  }, [open, replay]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Bienvenido al Protocolo"
      subtitle="Cuatro pasos para entrar, leer la arena y encender el Núcleo."
      wide
      footer={
        <>
          <button type="button" className="button button--ghost" onClick={onClose}>
            Saltar
          </button>
          <div className="tutorial__nav">
            {step > 0 && (
              <button type="button" className="button button--secondary" onClick={() => setStep((value) => value - 1)}>
                Atrás
              </button>
            )}
            <button
              type="button"
              className="button button--primary"
              onClick={() => {
                if (last) onClose();
                else setStep((value) => value + 1);
              }}
            >
              {last ? 'Entrar en la arena' : 'Siguiente'} <Icon name={last ? 'play' : 'chevron-right'} size={16} />
            </button>
          </div>
        </>
      }
    >
      <div className="tutorial">
        <div className={`tutorial__art tutorial__art--${current.icon}`} aria-hidden="true">
          <span className="tutorial__ring" />
          <span className="tutorial__ring tutorial__ring--two" />
          <span className="tutorial__icon"><Icon name={current.icon} size={48} /></span>
          <span className="tutorial__spark tutorial__spark--one">✦</span>
          <span className="tutorial__spark tutorial__spark--two">✧</span>
        </div>
        <div className="tutorial__copy">
          <span className="eyebrow">{current.kicker}</span>
          <h2>{current.title}</h2>
          <p>{current.text}</p>
          <div className="tutorial__chips">
            {current.chips.map((chip) => <span key={chip}>{chip}</span>)}
          </div>
          <div className="tutorial__progress" aria-label={`Paso ${step + 1} de ${STEPS.length}`}>
            {STEPS.map((entry, index) => (
              <button
                key={entry.kicker}
                type="button"
                className={index === step ? 'is-active' : index < step ? 'is-done' : ''}
                onClick={() => setStep(index)}
                aria-label={`Ir al paso ${index + 1}: ${entry.title}`}
              />
            ))}
          </div>
        </div>
      </div>
    </Modal>
  );
}
